import React, { Fragment, type ReactNode, useEffect, useId, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
	carousel: {
		id: 'smart-knowledge-cards.carousel.label',
		defaultMessage: 'Images',
		description: 'Accessible label for the image carousel region',
	},
	scrollLeft: {
		id: 'smart-knowledge-cards.carousel.scroll_left',
		defaultMessage: 'Scroll left',
		description:
			'Accessible label for the icon button in the image carousel header that scrolls the carousel content to the left.',
	},
	scrollRight: {
		id: 'smart-knowledge-cards.carousel.scroll_right',
		defaultMessage: 'Scroll right',
		description:
			'Accessible label for the icon button in the image carousel header that scrolls the carousel content to the right.',
	},
});

const styles = cssMap({
	buttonsInline: {
		marginLeft: 'auto',
	},
	headerInline: {
		minHeight: '24px',
	},
	scrollContainerStyle: {
		alignItems: 'stretch',
		borderRadius: token('radius.large'),
		display: 'flex',
		flexDirection: 'row',
		// NOTE: images.tsx calc() widths depend on this gap value — update both if changing
		gap: token('space.100'),
		justifyContent: 'flex-start',
		overflowX: 'auto',
		scrollSnapType: 'x mandatory',
		scrollbarWidth: 'none',
		whiteSpace: 'nowrap',
		// Prevent focus rings on child images from being clipped by the overflow boundary.
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
		marginInline: token('space.negative.050'),
		scrollPaddingInline: token('space.050'),
	},
});

const isScrollable = (elem: HTMLDivElement | null) =>
	elem ? elem.scrollWidth > elem.clientWidth : false;
const isScrollAtLeftEdge = (elem: HTMLDivElement | null) => (elem ? elem.scrollLeft <= 1 : false);
const isScrollAtRightEdge = (elem: HTMLDivElement | null) =>
	elem ? elem?.scrollWidth - elem?.scrollLeft - elem?.clientWidth <= 1 : false;

export const Carousel = function Carousel({
	children,
	heading,
	onArrowClick,
}: {
	children: ReactNode;
	heading?: string;
	onArrowClick?: () => void;
}): React.JSX.Element {
	const { formatMessage } = useIntl();
	const headingId = useId();
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const [isContentScrollable, setIsContentScrollable] = useState(false);
	const [shouldDisableLeftArrowButton, setShouldDisableLeftArrowButton] = useState(true);
	const [shouldDisableRightArrowButton, setShouldDisableRightArrowButton] = useState(false);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) {
			return;
		}

		const updateScrollState = () => {
			setIsContentScrollable(isScrollable(container));
			setShouldDisableLeftArrowButton(isScrollAtLeftEdge(container));
			setShouldDisableRightArrowButton(isScrollAtRightEdge(container));
		};

		updateScrollState();

		const resizeObserver = new ResizeObserver(updateScrollState);
		resizeObserver.observe(container);
		Array.from(container.children).forEach((child) => resizeObserver.observe(child));

		return () => resizeObserver.disconnect();
	}, []);

	const [onScroll] = useDebouncedCallback(() => {
		setShouldDisableLeftArrowButton(isScrollAtLeftEdge(scrollContainerRef.current));
		setShouldDisableRightArrowButton(isScrollAtRightEdge(scrollContainerRef.current));
	}, 10);

	const scrollLeft = () => {
		const container = scrollContainerRef.current;
		if (!container) {
			return;
		}
		// Scroll roughly one viewport width left — CSS scroll-snap will snap to the nearest item
		setShouldDisableRightArrowButton(false);
		container.scrollBy({ left: -container.clientWidth, behavior: 'smooth' });
		onArrowClick?.();
	};

	const scrollRight = () => {
		const container = scrollContainerRef.current;
		if (!container) {
			return;
		}
		// Scroll roughly one viewport width right — CSS scroll-snap will snap to the nearest item
		setShouldDisableLeftArrowButton(false);
		container.scrollBy({ left: container.clientWidth, behavior: 'smooth' });
		onArrowClick?.();
	};

	return (
		<Stack
			space="space.100"
			role={isContentScrollable ? 'region' : undefined}
			aria-roledescription={isContentScrollable ? 'carousel' : undefined}
			aria-labelledby={isContentScrollable && heading ? headingId : undefined}
			aria-label={isContentScrollable && !heading ? formatMessage(messages.carousel) : undefined}
		>
			<Inline spread="space-between" alignBlock="center" xcss={styles.headerInline}>
				{heading ? (
					<Heading as="h2" size="xsmall" id={headingId}>
						{heading}
					</Heading>
				) : null}
				<Inline space="space.050" xcss={styles.buttonsInline}>
					{isContentScrollable ? (
						<Fragment>
							<IconButton
								shape="circle"
								appearance="subtle"
								icon={ArrowLeftIcon}
								isTooltipDisabled
								label={formatMessage(messages.scrollLeft)}
								onClick={scrollLeft}
								spacing="compact"
								isDisabled={shouldDisableLeftArrowButton}
							/>
							<IconButton
								shape="circle"
								appearance="subtle"
								icon={ArrowRightIcon}
								isTooltipDisabled
								label={formatMessage(messages.scrollRight)}
								onClick={scrollRight}
								spacing="compact"
								isDisabled={shouldDisableRightArrowButton}
							/>
						</Fragment>
					) : null}
				</Inline>
			</Inline>

			<Box
				ref={scrollContainerRef}
				onScroll={onScroll}
				xcss={styles.scrollContainerStyle}
				tabIndex={isContentScrollable ? 0 : undefined}
			>
				{children}
			</Box>
		</Stack>
	);
};
